import { Injectable } from '@angular/core';
import { WindowRefService } from './windowRef.service';
import { JiraIssue, JiraIssueDTO } from '../models/jira-issue.model';
import { JiraProject } from '../models/jira-project.model';
import { JiraUser, JiraUserDTO } from '../models/jira-user.model';
import { JiraIssueTypeDTO } from '../models/jira-issuetype.model';
import { JiraBoardDTO } from '../models/jira-board.model';
import { HubstaffTimeEntryDTO } from '../models/hubstaff-entities.model';
import moment from 'moment';
import { JiraWorklogDTO } from '../models/jira-worklog.model';

interface ReqParams {
  url?: string;
  type?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class JiraApiService {
  constructor(private windowRefService: WindowRefService) {}

  async boardByProject(projectKey: string): Promise<JiraBoardDTO> {
    const queryParams = { projectKeyOrId: projectKey };
    const params = this.buildRequestParams(``, {
      url: '/rest/agile/1.0/board',
      data: queryParams,
    });
    const response = await this.makeRequest(params);

    const { values = [] } = response;

    if (values.length === 0) {
      throw Error('No board values are found');
    }

    return values[0] as JiraBoardDTO;
  }

  async issue(
    key: string,
    queryParams = { fields: 'summary,description,labels,project,issuetype', expand: 'renderedFields' },
  ): Promise<JiraIssue> {
    const params = this.buildRequestParams(`/issue/${key}`, { data: queryParams });
    const issue = await this.makeRequest(params);

    const { fields, renderedFields } = issue;

    const issueDTO: JiraIssueDTO = {
      key: issue.key,
      summary: fields?.summary ? fields.summary : 'no summary',
      labels: fields?.labels ? fields.labels.map((label: string) => ({ name: label })) : [],
      project: fields.project?.name
        ? { key: fields.project.key, name: fields.project.name, id: fields.project.id }
        : undefined,
      issueType: fields?.issuetype ? fields.issuetype : { name: 'no-issue-type' },
      htmlDescription: renderedFields?.description,
    };

    return new JiraIssue(issueDTO);
  }

  async project(projectKey: string): Promise<JiraProject> {
    const params = this.buildRequestParams(`/project/${projectKey}`);

    const project = await this.makeRequest(params);

    return new JiraProject(project);
  }

  async issueTypesForProject({ projectId }: { projectId: string }): Promise<JiraIssueTypeDTO[]> {
    const params = this.buildRequestParams(`/issuetype/project/?projectId=${projectId}`);

    return (await this.makeRequest(params)) as JiraIssueTypeDTO[];
  }

  async myself(): Promise<JiraUser> {
    const params = this.buildRequestParams(`/myself`);

    const myself = await this.makeRequest(params);

    return new JiraUser(myself);
  }

  async users(userAccountIds: string[]): Promise<JiraUser[]> {
    const params = this.buildRequestParams(`/user/bulk?accountId=${userAccountIds.join('&accountId=')}`, {
      data: {
        maxResults: 50,
      },
    });

    const response = await this.makeRequest(params);
    const { values = [] } = response;

    const users = values.map((valueAsUser: JiraUserDTO) => new JiraUser(valueAsUser));

    return users;
  }

  prepareWorklogItem(timeEntryDTO: HubstaffTimeEntryDTO): JiraWorklogDTO {
    const comment = timeEntryDTO.description || `Hubstaff activity`;

    return {
      timeSpentSeconds: timeEntryDTO.tracked || 60,
      started: this.formatToJiraDateTime(timeEntryDTO.startsAt || ''),
      comment: {
        content: [
          {
            type: 'paragraph',
            content: [{ type: 'status', attrs: { text: comment, color: 'purple', style: '' } }],
          },
          {
            type: 'paragraph',
            content: [{ type: 'text', text: ` - Project : ${timeEntryDTO.projectName}` }],
          },
          {
            type: 'paragraph',
            content: [{ type: 'text', text: ` - Task : ${timeEntryDTO.taskName || 'No to-do'}` }],
          },
        ],
        type: 'doc',
        version: 1,
      },
    };
  }

  private formatToJiraDateTime(dateTimeAsString: string): string {
    return moment(dateTimeAsString).format('YYYY-MM-DDTHH:mm:ss.000ZZ');
  }

  async addWorklogItem(issueKey: string, worklogItemDTO: JiraWorklogDTO): Promise<JiraWorklogDTO> {
    const params = this.buildRequestParams(`/issue/${issueKey}/worklog`, {
      type: 'POST',
      data: JSON.stringify(worklogItemDTO),
    });

    const worklogItem = await this.makeRequest(params);
    worklogItemDTO.id = worklogItem.id;

    return worklogItemDTO;
  }

  async deleteWorklogItem(issueKey: string, worklogItemId: string): Promise<void> {
    const params = this.buildRequestParams(`/issue/${issueKey}/worklog/${worklogItemId}`, {
      type: 'DELETE',
    });

    await this.makeRequest(params);
  }

  private buildRequestParams(path: string, params: ReqParams = {}): object {
    const defaultParams = {
      url: '/rest/api/3',
      type: 'GET',
      contentType: 'application/json',
    };
    defaultParams.url += path;

    return Object.assign({}, defaultParams, params);
  }

  private handleResponse(response: any): object {
    let { body = '{}' } = response;
    body = body === '' ? '{}' : body;

    return JSON.parse(body);
  }

  private async makeRequest(params: ReqParams): Promise<any> {
    const response = await this.windowRefService.AP.request(params);

    return this.handleResponse(response);
  }
}
